// Shared shape helpers for the small swarm families (Array Footman, Rift Scrabbler, Rift Moth). Render-only.
import * as THREE from 'three/webgpu';

type V2 = [number, number];
type V3 = [number, number, number];

/**
 * A flat extruded slab from a 2D outline (x, y), `depth` thick, centred on z = 0.
 * Use for blades, claws, banners, crests and fins. The outline should be simple (no self-intersections).
 */
export function slab(pts: V2[], depth: number): THREE.BufferGeometry {
  const s = new THREE.Shape(pts.map(([x, y]) => new THREE.Vector2(x, y)));
  const g = new THREE.ExtrudeGeometry(s, { depth, bevelEnabled: false, curveSegments: 1, steps: 1 });
  g.translate(0, 0, -depth / 2);
  return g;
}

/** Orient a slab authored in the XY plane so its outline lies in the YZ plane (outline x becomes +Z). */
export function sideways(g: THREE.BufferGeometry): THREE.BufferGeometry {
  return g.rotateY(-Math.PI / 2);
}

/**
 * A faceted, pleated wing: a fan of triangles from `root` through the rim points, with alternate rim points lifted by
 * `pleat` so flat shading shows glassy facets. Two-sided (top fan + bottom fan, `thick` apart).
 */
export function facetWing(root: V3, rim: V3[], pleat: number, thick = 0.012): THREE.BufferGeometry {
  const pos: number[] = [];
  const rp = rim.map((p, i) => [p[0], p[1] + (i % 2 ? pleat : 0), p[2]] as V3);
  for (let i = 0; i < rp.length - 1; i++) {
    const a = rp[i], b = rp[i + 1];
    // top (normal up for a wing spreading toward +x with the rim ordered front to back)
    pos.push(root[0], root[1] + thick / 2, root[2], a[0], a[1] + thick / 2, a[2], b[0], b[1] + thick / 2, b[2]);
    pos.push(root[0], root[1] - thick / 2, root[2], b[0], b[1] - thick / 2, b[2], a[0], a[1] - thick / 2, a[2]);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  return g;
}

/** Mirror a geometry across x = 0 (fixes winding). */
export function mirrorX(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const m = g.index ? g.toNonIndexed() : g.clone();
  m.scale(-1, 1, 1);
  const p = m.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i += 3) {
    const x = p.getX(i + 1), y = p.getY(i + 1), z = p.getZ(i + 1);
    p.setXYZ(i + 1, p.getX(i + 2), p.getY(i + 2), p.getZ(i + 2)); p.setXYZ(i + 2, x, y, z);
  }
  return m;
}

/** A low crystal shard (elongated octahedron-like bipyramid with `n` sides) from base to tip. */
export function shard(base: V3, tip: V3, r: number, n = 5): THREE.BufferGeometry {
  const A = new THREE.Vector3(...base), B = new THREE.Vector3(...tip), d = B.clone().sub(A), len = d.length();
  const g = new THREE.ConeGeometry(r, len * 0.75, n, 1, true);
  g.translate(0, len * 0.375 + len * 0.25, 0);
  const foot = new THREE.ConeGeometry(r, len * 0.25, n, 1, true); foot.rotateX(Math.PI); foot.translate(0, len * 0.125, 0);
  const merged = new THREE.BufferGeometry();
  const a = g.toNonIndexed(), b = foot.toNonIndexed();
  const arr = new Float32Array(a.attributes.position.array.length + b.attributes.position.array.length);
  arr.set(a.attributes.position.array as Float32Array, 0); arr.set(b.attributes.position.array as Float32Array, a.attributes.position.array.length);
  merged.setAttribute('position', new THREE.BufferAttribute(arr, 3));
  merged.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize()));
  return merged.translate(A.x, A.y, A.z);
}

/** Triangle count of a built variant (body + glow), for budget checks. */
export function triCount(g: { body: THREE.BufferGeometry; glow: THREE.BufferGeometry }): number {
  const t = (x: THREE.BufferGeometry) => (x.index ? x.index.count : x.attributes.position.count) / 3;
  return Math.round(t(g.body) + t(g.glow));
}

/**
 * Outer band of a faceted wing between an inner ring and the rim (both ordered front to back, same length).
 * Alternate rim points are lifted by `pleat`. Two-sided like facetWing.
 */
export function wingBand(inner: V3[], rim: V3[], pleat: number, thick = 0.012): THREE.BufferGeometry {
  const pos: number[] = [], h = thick / 2;
  const o = rim.map((p, i) => [p[0], p[1] + (i % 2 ? pleat : 0), p[2]] as V3);
  const tri = (a: V3, b: V3, c: V3) => {
    pos.push(a[0], a[1] + h, a[2], b[0], b[1] + h, b[2], c[0], c[1] + h, c[2]);
    pos.push(a[0], a[1] - h, a[2], c[0], c[1] - h, c[2], b[0], b[1] - h, b[2]);
  };
  for (let i = 0; i < o.length - 1; i++) { tri(inner[i], o[i], o[i + 1]); tri(inner[i], o[i + 1], inner[i + 1]); }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  return g;
}

/** Uniformly scale a built variant about the origin (positions and limb pivots), keeping feet on y = 0. */
export function scaleArt(g: { body: THREE.BufferGeometry; glow: THREE.BufferGeometry }, k: number): typeof g {
  for (const geo of [g.body, g.glow]) {
    for (const name of ['position', 'pivot']) {
      const a = geo.getAttribute(name) as THREE.BufferAttribute | undefined; if (!a) continue;
      for (let i = 0; i < a.array.length; i++) (a.array as Float32Array)[i] *= k;
      a.needsUpdate = true;
    }
    geo.computeBoundingSphere();
  }
  return g;
}
